import React from 'react';
import PropTypes from 'prop-types';
import { Text } from '../../Atoms/Text';
import Select, { components } from 'react-select';
import { useTheme } from '@mui/material/styles';

const selectStyles = (theme) => ({
  multiValue: () => ({
    alignItems: 'center',
    height: '26px',
    display: 'flex',
    background: theme.palette.action.selected,
    paddingLeft: '10px',
    borderRadius: theme.shape.borderRadius,
    margin: '2px',
  }),
  multiValueLabel: () => ({
    fontSize: '14px',
    color: theme.palette.text.secondary,
  }),
  multiValueRemove: () => ({
    borderRadius: '50%',
    padding: '2px',
    backgroundColor: theme.palette.grey[400],
    margin: '6px',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  }),
  menu: () => ({
    zIndex: theme.zIndex.modal,
    position: 'absolute',
    width: '100%',
    boxShadow: theme.shadows[1],
  }),
  menuList: () => ({
    backgroundColor: theme.palette.background.paper,
    maxHeight: '300px',
    overflowY: 'auto',
    position: 'relative',
    boxSizing: 'border-box',
  }),
  option: (styles, { isFocused }) => ({
    color: theme.palette.text.primary,
    backgroundColor: isFocused ? theme.palette.action.hover : 'transparent',
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
    cursor: 'pointer',
  }),
  control: () => ({
    display: 'flex',
    flexWrap: 'wrap',
    minHeight: '38px',
    borderRadius: '4px',
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
  }),
  input: () => ({
    color: theme.palette.text.primary,
  }),
});

function MenuGenerator({ maxLength, text }) {
  return function Menu(menuProps) {
    const optionSelectedLength = menuProps.getValue().length || 0;

    return (
      <components.Menu {...menuProps}>
        {optionSelectedLength < maxLength ? (
          menuProps.children
        ) : (
          <Text textAlign="center" m={2}>
            {text}
          </Text>
        )}
      </components.Menu>
    );
  };
}

export function SkillSelector(props) {
  const {
    name,
    placeholder,
    onChange,
    skills,
    values,
    className,
    id,
    maximum,
  } = props;

  const maxLength = maximum?.length ?? Infinity;
  const text = maximum?.text;
  const Menu = MenuGenerator({ maxLength, text });
  const isValidNewOption = (inputValue, selectValue) =>
    selectValue.length < maxLength;

  const theme = useTheme();
  const styles = selectStyles(theme);
  return (
    <Select
      id={id}
      styles={styles}
      closeOnSelect={true}
      name={name}
      isMulti
      joinValues={true}
      onChange={onChange}
      options={skills}
      placeholder={placeholder}
      removeSelected={true}
      simpleValue
      defaultValue={values}
      className={className}
      clearable={true}
      isValidNewOption={isValidNewOption}
      isSearchable={(values?.length || 0) < maxLength}
      components={{
        Menu,
      }}
    />
  );
}

SkillSelector.propTypes = {
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  values: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  maximum: PropTypes.shape({
    text: PropTypes.node.isRequired,
    length: PropTypes.number.isRequired,
  }),
  className: PropTypes.string,
  placeholder: PropTypes.string,
  id: PropTypes.string,
};

SkillSelector.defaultProps = {
  placeholder: 'e.g. React, Laravel, Node.js, Go, Django, Kotlin, etc.',
};
